import { t } from "@/lib/i18n";
import { CourseAudioPrefetch, useCourseDeck } from "@/review/course-study";
import { useWeapon } from "../core/weapon";
import { TopPageLayout } from "@/components/TopPageLayout";
import { DeckLoadStatus } from "@/app/DeckPage";
import type { AppContextType } from "@/app/context";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import {
  type Deck,
  type Weapon,
  type Language,
  type DictionaryWord,
  type DictionarySense,
} from "../../../yap-frontend-rs/pkg";
import { CirclePlus, CircleCheckBig, Search } from "lucide-react";
import { toast } from "sonner";
import { highlightTermInSentence } from "@/utils/highlightTermInSentence";
import { Card } from "@/components/ui/card";
import { LANGUAGES } from "@/lib/languages";
import { Ruby, TargetLanguageText } from "../components/TargetLanguageText";
import { AudioButton } from "../audio/AudioButton";

const RESULTS_LIMIT = 100;

export function Dictionary({
  deck,
  weapon,
  targetLanguage,
  nativeLanguage,
  accessToken,
}: {
  deck: Deck;
  weapon: Weapon;
  targetLanguage: Language;
  nativeLanguage: Language;
  accessToken: string | undefined;
}) {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [justAdded, setJustAdded] = useState<Set<number>>(new Set());
  const [optimisticDeck, setOptimisticDeck] = useState(deck);
  // Keep the search, but let each fresh snapshot replace optimistic badges.
  // In particular, an addition to the old account must not disable the new one's button.
  if (optimisticDeck !== deck) {
    setOptimisticDeck(deck);
    setJustAdded(new Set());
  }

  const totalCount = deck.get_gram_dictionary_count();
  const entries = deck.get_gram_dictionary_entries(
    searchQuery.trim() || undefined,
    RESULTS_LIMIT,
  );

  const { badge: targetLangCode, englishName: targetLangName } =
    LANGUAGES[targetLanguage];
  const { badge: nativeLangCode, englishName: nativeLangName } =
    LANGUAGES[nativeLanguage];

  const handleAddCard = (entry: DictionaryWord, sense: DictionarySense) => {
    const event = deck.add_gram_by_frequency_index(sense.frequency_index);
    if (event) {
      weapon.add_deck_event(event);
      setJustAdded((prev) => new Set(prev).add(sense.frequency_index));
      toast.success(t("dictionary-added-word", { word: entry.display_text }));
    }
  };

  const isCardAdded = (sense: DictionarySense) => {
    return sense.is_in_deck || justAdded.has(sense.frequency_index);
  };

  return (
    <div
      className="flex-1 overflow-hidden flex flex-col"
      data-page="dictionary"
    >
      <div className="border-b pb-4 mb-4 p-2">
        <div className="dictionary-search relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
            aria-hidden
          />
          <input
            type="search"
            placeholder={t("dictionary-search-placeholder", {
              target: String(targetLangName),
              native: String(nativeLangName),
            })}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            // Arriving from Home's search bar means the learner wants to type.
            autoFocus={location.state?.focusSearch === true}
            className="w-full h-10 pl-9 pr-3 border border-input rounded-lg bg-foreground/5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          {searchQuery
            ? t("dictionary-results-filtered", {
                shown: entries.length,
                count: totalCount,
                query: searchQuery,
              })
            : t("dictionary-results", {
                shown: entries.length,
                count: totalCount,
              })}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        <div className="space-y-4">
          {entries.map((entry) => (
            <Card key={entry.frequency_index} className="p-4 gap-4">
              <div className="flex items-center gap-2 min-w-0">
                <AudioButton
                  audioRequest={entry.audio_request}
                  accessToken={accessToken}
                  className="h-8 w-8 shrink-0"
                />
                <div className="flex flex-col gap-1 min-w-0">
                  <h2 className="text-xl font-semibold flex items-baseline gap-2">
                    <TargetLanguageText language={targetLanguage}>
                      {entry.prefix && (
                        <span className="text-muted-foreground/60">
                          {entry.prefix.prefix}
                          {entry.prefix.separator}
                        </span>
                      )}
                      <Ruby segments={entry.ruby}>{entry.display_text}</Ruby>
                    </TargetLanguageText>
                    {entry.is_phrase && (
                      <span className="text-sm text-muted-foreground/60 font-normal">
                        {t("dictionary-screen-phrase")}
                      </span>
                    )}
                  </h2>
                </div>
              </div>
              {entry.senses.map((dictionarySense) => {
                const definition = dictionarySense.definition;
                return (
                  <div
                    key={dictionarySense.frequency_index}
                    className="flex flex-col gap-3"
                  >
                    {definition.morphology_label && (
                      <span className="text-sm text-muted-foreground italic">
                        {definition.morphology_label}
                      </span>
                    )}

                    <div className="space-y-3">
                      {definition.senses.map((sense, index) => (
                        <div
                          key={index}
                          className="pl-4 border-l-2 border-muted flex flex-col gap-2"
                        >
                          <div className="flex flex-col gap-1">
                            <div className="font-medium text-primary">
                              {sense.meaning}
                            </div>
                            {sense.note && (
                              <div className="text-sm text-muted-foreground italic">
                                {sense.note}
                              </div>
                            )}
                          </div>
                          {sense.example && (
                            <div className="text-sm space-y-1">
                              <div className="text-foreground">
                                <span className="text-muted-foreground">
                                  {targetLangCode}:
                                </span>{" "}
                                <TargetLanguageText language={targetLanguage}>
                                  {highlightTermInSentence(
                                    sense.example.target,
                                    entry.display_text,
                                  )}
                                </TargetLanguageText>
                              </div>
                              <div className="text-muted-foreground">
                                <span>{nativeLangCode}:</span>{" "}
                                {sense.example.native}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-end">
                      {isCardAdded(dictionarySense) ? (
                        <button
                          disabled
                          className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground cursor-default"
                        >
                          <CircleCheckBig className="w-4 h-4" />
                          <span>{t("dictionary-screen-added")}</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleAddCard(entry, dictionarySense)}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-muted rounded-md transition-colors"
                        >
                          <CirclePlus className="w-4 h-4" />
                          <span>{t("dictionary-screen-add-to-deck")}</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </Card>
          ))}

          {entries.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              {searchQuery
                ? t("dictionary-screen-no-entries-found-matching-your-search")
                : t("dictionary-screen-no-dictionary-entries-available")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function DictionaryPage() {
  const { userInfo, accessToken } = useOutletContext<AppContextType>();
  const deck = useCourseDeck();
  const weapon = useWeapon();
  const navigate = useNavigate();

  const phase = deck.view.phase;
  useEffect(() => {
    if (phase.type === "NoLanguageSelected") navigate("/", { replace: true });
  }, [phase.type, navigate]);
  if (phase.type === "NoLanguageSelected") return null;
  if (phase.type !== "Ready" || !deck.deck || !deck.course) {
    return (
      <TopPageLayout
        userInfo={userInfo}
        headerProps={{
          backButton: {
            label: t("common-home"),
            onBack: () => navigate("/home"),
          },
        }}
      >
        <DeckLoadStatus phase={phase} retry={deck.retry} />
      </TopPageLayout>
    );
  }

  return (
    <TopPageLayout
      userInfo={userInfo}
      headerProps={{
        backButton: {
          label: t("common-home"),
          onBack: () => navigate("/home"),
        },
      }}
    >
      <CourseAudioPrefetch />
      <Dictionary
        deck={deck.deck}
        weapon={weapon}
        targetLanguage={deck.course.targetLanguage}
        nativeLanguage={deck.course.nativeLanguage}
        accessToken={accessToken}
      />
    </TopPageLayout>
  );
}
